import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Download, LogOut, RefreshCw, Search, ShieldCheck } from "lucide-react";
import AdminReportingPanel from "@/components/AdminReportingPanel";
import LeadEditorPanel from "@/components/LeadEditorPanel";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "koineckta_admin_token";

const formatDate = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

const brandLogo = "/koineckta-logo-primary.png";

export default function AdminPortal() {
  const [token, setToken] = useState(() => window.localStorage.getItem(STORAGE_KEY) || "");
  const [loginForm, setLoginForm] = useState({ email: "admin@koineckta.com", password: "" });
  const [filters, setFilters] = useState({ role: "all", search: "", page: 1, limit: 20, sort_by: "created_at", sort_direction: "desc" });
  const [reportingDays, setReportingDays] = useState(30);
  const [reporting, setReporting] = useState({
    total_verified: 0,
    total_pending: 0,
    total_signups: 0,
    conversion_rate: 0,
    verified_today: 0,
    pending_today: 0,
    country_breakdown: [],
    trend: [],
    status_breakdown: [],
  });
  const [leads, setLeads] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 20 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [authenticating, setAuthenticating] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [isSavingLead, setIsSavingLead] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  useEffect(() => {
    document.title = "Koineckta Admin | Internal Leads View";
  }, []);

  const authHeaders = useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);

  const loadDashboard = useCallback(async (nextToken, nextFilters, nextReportingDays = 30) => {
    if (!nextToken) return;
    setLoading(true);
    setError("");
    try {
      const params = {
        page: nextFilters.page,
        limit: nextFilters.limit,
        sort_by: nextFilters.sort_by,
        sort_direction: nextFilters.sort_direction,
      };
      if (nextFilters.role !== "all") params.role = nextFilters.role;
      if (nextFilters.search.trim()) params.search = nextFilters.search.trim();

      const [statsResponse, leadsResponse] = await Promise.all([
        axios.get(`${API}/admin/reporting`, { headers: { Authorization: `Bearer ${nextToken}` }, params: { days: nextReportingDays } }),
        axios.get(`${API}/admin/leads`, { headers: { Authorization: `Bearer ${nextToken}` }, params }),
      ]);

      setReporting(statsResponse.data);
      setLeads(leadsResponse.data.items || []);
      setPagination({
        total: leadsResponse.data.total,
        page: leadsResponse.data.page,
        limit: leadsResponse.data.limit,
      });
    } catch (requestError) {
      if (requestError?.response?.status === 401) {
        window.localStorage.removeItem(STORAGE_KEY);
        setToken("");
        setLeads([]);
        setSelectedLead(null);
      }
      setError(requestError?.response?.data?.detail || "Could not load the admin dashboard.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) {
      loadDashboard(token, filtersRef.current, reportingDays);
    }
  }, [token, reportingDays, loadDashboard]);

  const handleLogin = async (event) => {
    event.preventDefault();
    setAuthenticating(true);
    setError("");
    try {
      const response = await axios.post(`${API}/admin/login`, loginForm);
      window.localStorage.setItem(STORAGE_KEY, response.data.token);
      setToken(response.data.token);
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || "Login failed.");
    } finally {
      setAuthenticating(false);
    }
  };

  const handleLogout = () => {
    window.localStorage.removeItem(STORAGE_KEY);
    setToken("");
    setLeads([]);
  };

  const handleRefresh = () => {
    loadDashboard(token, filters, reportingDays);
  };

  const handleApplyFilters = () => {
    const nextFilters = { ...filters, page: 1 };
    setFilters(nextFilters);
    loadDashboard(token, nextFilters, reportingDays);
  };

  const handlePageChange = (nextPage) => {
    const nextFilters = { ...filters, page: nextPage };
    setFilters(nextFilters);
    loadDashboard(token, nextFilters, reportingDays);
  };

  const handleSaveLead = async (payload) => {
    if (!selectedLead) return;
    setIsSavingLead(true);
    setSaveMessage("");
    setError("");
    try {
      const response = await axios.patch(`${API}/admin/leads/${selectedLead.id}`, payload, { headers: authHeaders });
      setSelectedLead(response.data);
      setSaveMessage("Lead follow-up details saved.");
      await loadDashboard(token, filters, reportingDays);
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || "Could not save lead updates.");
    } finally {
      setIsSavingLead(false);
    }
  };

  const handleExport = async () => {
    try {
      const params = {};
      if (filters.role !== "all") params.role = filters.role;
      if (filters.search.trim()) params.search = filters.search.trim();
      params.sort_by = filters.sort_by;
      params.sort_direction = filters.sort_direction;

      const response = await axios.get(`${API}/admin/leads/export.csv`, {
        headers: authHeaders,
        params,
        responseType: "blob",
      });

      const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", "koineckta-leads.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || "Could not export the leads CSV.");
    }
  };

  if (!token) {
    return (
      <div className="app-shell min-h-screen overflow-x-hidden px-5 py-10 sm:px-8 lg:px-10" data-testid="admin-login-page">
        <motion.div
          className="mx-auto grid min-h-[calc(100vh-5rem)] max-w-6xl items-center gap-8 lg:grid-cols-[0.95fr_1.05fr]"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="space-y-6">
            <img src={brandLogo} alt="Koineckta logo" className="h-12 w-auto sm:h-14" data-testid="admin-login-logo-image" />
            <div className="inline-flex items-center gap-3 rounded-full border border-sky-200 bg-white/80 px-4 py-2 text-sm font-semibold text-sky-700 shadow-sm" data-testid="admin-login-badge">
              <ShieldCheck className="h-4 w-4" />
              Internal Koineckta Access
            </div>
            <h1 className="font-display text-4xl font-semibold leading-tight text-slate-950 sm:text-5xl" data-testid="admin-login-title">
              Review and export waitlist leads from one secure internal workspace.
            </h1>
            <p className="max-w-xl text-base leading-8 text-slate-600 sm:text-lg" data-testid="admin-login-description">
              This private dashboard gives Koineckta a simple way to sign in, view segmented leads, and export the list for follow-up.
            </p>
          </div>

          <Card className="rounded-[32px] border-[#3ABDBC]/20 bg-[#08133E]/88 shadow-[0_30px_90px_-40px_rgba(1,11,48,0.8)] backdrop-blur-xl" data-testid="admin-login-card">
            <CardContent className="p-8 sm:p-10">
              <form className="space-y-5" onSubmit={handleLogin} data-testid="admin-login-form">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700" htmlFor="admin-email" data-testid="admin-email-label">
                    Admin Email
                  </label>
                  <Input
                    id="admin-email"
                    type="email"
                    value={loginForm.email}
                    onChange={(event) => setLoginForm((current) => ({ ...current, email: event.target.value }))}
                    className="h-12 rounded-2xl border-slate-200 bg-white/90 px-4"
                    data-testid="admin-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700" htmlFor="admin-password" data-testid="admin-password-label">
                    Password
                  </label>
                  <Input
                    id="admin-password"
                    type="password"
                    value={loginForm.password}
                    onChange={(event) => setLoginForm((current) => ({ ...current, password: event.target.value }))}
                    className="h-12 rounded-2xl border-slate-200 bg-white/90 px-4"
                    data-testid="admin-password-input"
                  />
                </div>
                {error && <p className="text-sm text-red-600" data-testid="admin-login-error">{error}</p>}
                <Button
                  type="submit"
                  className="h-12 w-full rounded-full bg-slate-950 text-white hover:bg-slate-800"
                  disabled={authenticating}
                  data-testid="admin-login-submit-button"
                >
                  {authenticating ? "Signing in..." : "Sign In to Admin"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="app-shell min-h-screen overflow-x-hidden px-5 py-8 sm:px-8 lg:px-10" data-testid="admin-dashboard-page">
      <motion.div
        className="mx-auto max-w-7xl space-y-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.75, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="space-y-3">
            <img src={brandLogo} alt="Koineckta logo" className="h-12 w-auto sm:h-14" data-testid="admin-dashboard-logo-image" />
            <div className="inline-flex items-center gap-3 rounded-full border border-sky-200 bg-white/80 px-4 py-2 text-sm font-semibold text-sky-700 shadow-sm" data-testid="admin-dashboard-badge">
              <ShieldCheck className="h-4 w-4" />
              Koineckta Leads Console
            </div>
            <h1 className="font-display text-4xl font-semibold text-slate-950 sm:text-5xl" data-testid="admin-dashboard-title">
              Internal export and follow-up view
            </h1>
            <p className="max-w-3xl text-base leading-8 text-slate-600 sm:text-lg" data-testid="admin-dashboard-description">
              Review signups, filter by role, search across contacts, and export your current waitlist as CSV.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button onClick={handleRefresh} variant="outline" className="h-11 rounded-full border-slate-300 bg-white/80" data-testid="admin-refresh-button">
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
            <Button onClick={handleExport} className="h-11 rounded-full bg-slate-950 text-white hover:bg-slate-800" data-testid="admin-export-button">
              <Download className="mr-2 h-4 w-4" />
              Export CSV
            </Button>
            <Button onClick={handleLogout} variant="outline" className="h-11 rounded-full border-slate-300 bg-white/80" data-testid="admin-logout-button">
              <LogOut className="mr-2 h-4 w-4" />
              Log Out
            </Button>
          </div>
        </div>

        <AdminReportingPanel reporting={reporting} reportingDays={reportingDays} onDaysChange={setReportingDays} />

        <Card className="rounded-[32px] border-[#3ABDBC]/20 bg-[#08133E]/88 shadow-[0_24px_70px_-45px_rgba(1,11,48,0.75)] backdrop-blur-xl" data-testid="admin-filters-card">
          <CardContent className="grid gap-4 p-6 md:grid-cols-[1fr_220px_220px_220px_auto] md:items-end">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700" htmlFor="lead-search" data-testid="lead-search-label">
                Search leads
              </label>
              <div className="relative">
                <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  id="lead-search"
                  value={filters.search}
                  onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))}
                  placeholder="Search by name, email, business, or country"
                  className="h-12 rounded-2xl border-slate-200 bg-white/90 pl-11"
                  data-testid="lead-search-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700" htmlFor="lead-role-filter" data-testid="lead-role-filter-label">
                Role filter
              </label>
              <select
                id="lead-role-filter"
                value={filters.role}
                onChange={(event) => setFilters((current) => ({ ...current, role: event.target.value }))}
                className="h-12 w-full rounded-2xl border border-slate-200 bg-white/90 px-4 text-sm text-slate-900"
                data-testid="lead-role-filter-select"
              >
                <option value="all">All leads</option>
                <option value="merchant">Merchants</option>
                <option value="individual">Individuals</option>
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700" htmlFor="lead-sort-field" data-testid="lead-sort-field-label">
                Sort by
              </label>
              <select
                id="lead-sort-field"
                value={filters.sort_by}
                onChange={(event) => setFilters((current) => ({ ...current, sort_by: event.target.value }))}
                className="h-12 w-full rounded-2xl border border-slate-200 bg-white/90 px-4 text-sm text-slate-900"
                data-testid="lead-sort-field-select"
              >
                <option value="created_at">Newest / oldest</option>
                <option value="full_name">Name</option>
                <option value="country">Country</option>
                <option value="role">Role</option>
                <option value="status">Status</option>
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700" htmlFor="lead-sort-direction" data-testid="lead-sort-direction-label">
                Direction
              </label>
              <select
                id="lead-sort-direction"
                value={filters.sort_direction}
                onChange={(event) => setFilters((current) => ({ ...current, sort_direction: event.target.value }))}
                className="h-12 w-full rounded-2xl border border-slate-200 bg-white/90 px-4 text-sm text-slate-900"
                data-testid="lead-sort-direction-select"
              >
                <option value="desc">Descending</option>
                <option value="asc">Ascending</option>
              </select>
            </div>
            <Button onClick={handleApplyFilters} className="h-12 rounded-full bg-slate-950 text-white hover:bg-slate-800" data-testid="lead-apply-filters-button">
              Apply Filters
            </Button>
          </CardContent>
        </Card>

        {error && <p className="text-sm text-red-600" data-testid="admin-dashboard-error">{error}</p>}

        <Card className="overflow-hidden rounded-[32px] border-[#3ABDBC]/20 bg-[#08133E]/88 shadow-[0_24px_70px_-45px_rgba(1,11,48,0.75)] backdrop-blur-xl" data-testid="admin-leads-table-card">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="min-w-full text-left" data-testid="admin-leads-table">
                <thead className="bg-slate-950 text-white">
                  <tr>
                    {[
                      "Date",
                      "Role",
                      "Status",
                      "Name",
                      "Email",
                      "Country",
                      "Business / Profile",
                      "Intent",
                      "Actions",
                    ].map((heading) => (
                      <th key={heading} className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.18em]" data-testid={`admin-table-heading-${heading.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                        {heading}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="9" className="px-5 py-10 text-center text-sm text-slate-500" data-testid="admin-table-loading-state">
                        Loading leads...
                      </td>
                    </tr>
                  ) : leads.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="px-5 py-10 text-center text-sm text-slate-500" data-testid="admin-table-empty-state">
                        No leads matched your current filters.
                      </td>
                    </tr>
                  ) : (
                    leads.map((lead) => (
                      <tr key={lead.id} className="border-b border-[#26345F] align-top transition-colors duration-200 hover:bg-[#121E4E]" data-testid={`admin-lead-row-${lead.id}`}>
                        <td className="px-5 py-4 text-sm text-slate-600">{formatDate(lead.created_at)}</td>
                        <td className="px-5 py-4">
                          <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] ${lead.role === "merchant" ? "bg-sky-100 text-sky-700" : "bg-orange-100 text-orange-700"}`} data-testid={`admin-lead-role-${lead.id}`}>
                            {lead.role}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-sm text-slate-700">{lead.status}</td>
                        <td className="px-5 py-4 text-sm font-semibold text-slate-900">{lead.full_name}</td>
                        <td className="px-5 py-4 text-sm text-slate-700">{lead.email}</td>
                        <td className="px-5 py-4 text-sm text-slate-700">{lead.country}</td>
                        <td className="px-5 py-4 text-sm text-slate-700">
                          {lead.role === "merchant"
                            ? lead.business_name || "—"
                            : lead.individual_roles?.join(", ") || "—"}
                          {lead.role === "merchant" && lead.pilot_readiness && <span className="mt-1 block text-xs text-[#6AE32F]" data-testid={`admin-lead-readiness-${lead.id}`}>{lead.pilot_readiness}</span>}
                        </td>
                        <td className="max-w-xs px-5 py-4 text-sm text-slate-600">{lead.usage_plan || "—"}</td>
                        <td className="px-5 py-4">
                          <Button
                            type="button"
                            variant="outline"
                            className="h-10 rounded-full border-slate-300 bg-white/90"
                            onClick={() => {
                              setSelectedLead(lead);
                              setSaveMessage("");
                            }}
                            data-testid={`admin-manage-lead-${lead.id}`}
                          >
                            Manage
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {saveMessage && <p className="text-sm text-emerald-700" data-testid="lead-save-success-message">{saveMessage}</p>}
        <LeadEditorPanel lead={selectedLead} saving={isSavingLead} onClose={() => setSelectedLead(null)} onSave={handleSaveLead} />

        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <p className="text-sm text-slate-500" data-testid="admin-pagination-summary">
            Showing page {pagination.page} • {leads.length} visible leads • {pagination.total} total matches
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-slate-600" htmlFor="lead-page-size" data-testid="lead-page-size-label">Rows</label>
              <select
                id="lead-page-size"
                value={filters.limit}
                onChange={(event) => {
                  const nextFilters = { ...filters, limit: Number(event.target.value), page: 1 };
                  setFilters(nextFilters);
                  loadDashboard(token, nextFilters, reportingDays);
                }}
                className="h-11 rounded-full border border-slate-300 bg-white px-4 text-sm text-slate-900"
                data-testid="lead-page-size-select"
              >
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() => handlePageChange(Math.max(1, pagination.page - 1))}
              disabled={pagination.page === 1 || loading}
              className="h-11 rounded-full border-slate-300 bg-white/80"
              data-testid="lead-previous-page-button"
            >
              Previous
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={loading || pagination.page * pagination.limit >= pagination.total}
              className="h-11 rounded-full border-slate-300 bg-white/80"
              data-testid="lead-next-page-button"
            >
              Next
            </Button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}